import React from 'react'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../../StaticText/StaticTextContext'
import PlaylistOutline from '../../../../Icon/icons/PlaylistOutline/index'
import Clock from '../../../../Icon/icons/Clock/index'
import type { StaticTextMap } from '../../../../../services/community/types'

interface CommunityPlaylistRowHeaderBaseProps {
  name: string
  badge?: { label: string; variant: 'progress' | 'topic' } | null
  episodeCount?: number
  totalDuration?: string | null
  authorDisplay?: string | null
  authorUid?: string | null
  followerCount?: number
  /**
   * Legacy single-slot right-aligned action in the name row. Used when the
   * consumer has no `actions` cluster (e.g. SystemPlaylistRow).
   * When `actions` is provided, it takes precedence and this prop is ignored.
   */
  nameAction?: React.ReactNode
  /**
   * Structured right-aligned cluster rendered inside `__name-row` at the
   * right edge. Typically `[nameAction, statusTag, shareButton]`.
   */
  actions?: React.ReactNode
  /**
   * Optional row rendered between the name row and the stats row. Used by
   * the wrapper at mobile breakpoints to drop the status tag (follow
   * tag / sharing dropdown) to its own line below the title.
   */
  statusTagRow?: React.ReactNode
  /**
   * Optional replacement for the default `playlist-row-header__stats` block.
   * When provided, the consumer's node is rendered in place of the default
   * stats row (e.g. Currently Watching renders series count + overall
   * progress instead of episodes · duration · author).
   */
  customStats?: React.ReactNode
  /**
   * When true, the default stats row renders invisible placeholder slots
   * (episodes + duration) that reserve the same vertical space the resolved
   * stats will occupy after hydration. Mirrors the `CommunityTabBar`
   * `--placeholder` idiom — keeps the row's height stable across the
   * hydration window so children below don't shift. Ignored when
   * `customStats` is provided (the consumer owns the slot).
   */
  statsPending?: boolean
  editMode?: boolean
  editingName?: string
  onNameChange?: ((value: string) => void) | null
}

interface CommunityPlaylistRowHeaderOuterProps extends CommunityPlaylistRowHeaderBaseProps {
  staticText: StaticTextMap
}

// Inner component: lives inside the Outer's <StaticTextProvider>, so
// useTranslatedText resolves against the `playlistRowHeader` slice we
// provide. The follower-count templates and episode-count template are
// resolved unconditionally per Rules of Hooks; results are picked at render
// time based on followerCount / episodeCount.
function CommunityPlaylistRowHeaderInner ({
  name,
  badge = null,
  episodeCount = 0,
  totalDuration = null,
  authorDisplay = null,
  authorUid = null,
  followerCount = 0,
  nameAction,
  actions,
  statusTagRow,
  customStats,
  statsPending = false,
  editMode = false,
  editingName,
  onNameChange = null,
}: CommunityPlaylistRowHeaderBaseProps) {
  const followerSingularText = useTranslatedText({
    keys: ['followerCount'],
    params: { count: String(followerCount) },
  })
  const followerPluralText = useTranslatedText({
    keys: ['followersCount'],
    params: { count: String(followerCount) },
  })
  const episodeCountText = useTranslatedText({
    keys: ['episodeCount'],
    params: { count: String(episodeCount) },
  })
  const playlistNameAriaLabel = useTranslatedText({ keys: ['playlistName'] })

  function renderFollowerText () {
    if (followerCount <= 0) return null
    return followerCount === 1 ? followerSingularText : followerPluralText
  }

  function renderAuthorName () {
    if (!authorDisplay) return null
    if (!authorUid) return authorDisplay
    return (
      <a href={`/profile/${authorUid}`} data-testid="author-profile-link">
        {authorDisplay}
      </a>
    )
  }

  function renderAuthorStat () {
    if (authorDisplay) {
      return (
        <span className="playlist-row-header__stat playlist-row-header__stat--author">
          <span className="playlist-row-header__stat-author-name">
            {renderAuthorName()}
          </span>
          {followerCount > 0 ? (
            <>
              <span className="playlist-row-header__meta-dot">{'·'}</span>
              {renderFollowerText()}
            </>
          ) : null}
        </span>
      )
    }
    if (followerCount > 0) {
      return (
        <span className="playlist-row-header__stat">
          {renderFollowerText()}
        </span>
      )
    }
    return null
  }

  return (
    <div
      className={`playlist-row-header${editMode ? ' playlist-row-header--editing' : ''}`}
      data-testid="playlist-row-header"
    >
      <div className="playlist-row-header__info">
        <div className="playlist-row-header__name-row">
          {editMode && onNameChange ? (
            <input
              type="text"
              className="playlist-row-header__name-input"
              value={editingName ?? name}
              onChange={(e) => onNameChange(e.target.value)}
              aria-label={playlistNameAriaLabel}
            />
          ) : (
            <span className="playlist-row-header__name">{name}</span>
          )}
          {badge ? (
            <span className={`playlist-row-header__badge playlist-row-header__badge--${badge.variant}`}>
              {badge.label}
            </span>
          ) : null}
          {/*
            Right-aligned action cluster. When structured `actions` are
            provided, render them inside an `__actions` wrapper at the right
            edge of the name row. Otherwise fall back to the legacy single
            `nameAction` pill (SystemPlaylistRow path).
          */}
          {actions ? <div className="community-playlist-row-header__actions">{actions}</div> : null}
          {!actions && nameAction ? (
            <span className="playlist-row-header__name-action">{nameAction}</span>
          ) : null}
        </div>
        {/*
          Optional dedicated row for the status tag (follow / sharing)
          beneath the name row. Used at mobile widths so the status tag
          does not compete with title + share button for horizontal space.
        */}
        {statusTagRow ? (
          <div className="community-playlist-row-header__status-tag-row">{statusTagRow}</div>
        ) : null}
        {customStats !== undefined ? customStats : (
          <div className="playlist-row-header__stats">
            {statsPending ? (
              <>
                {/*
                  Hydration-pending placeholder slots. The real stats (episodes,
                  duration, author) appear once `useRowHydration` resolves. To
                  prevent the row's height from growing at that moment — and
                  shifting every sibling row below — we render two invisible
                  __stat spans here that share the same icon + line-box as the
                  resolved markup. Mirrors the CommunityTabBar `--placeholder`
                  idiom (see _CommunityTabBar.scss). Author is intentionally
                  omitted: author data arrives with the shell, not at hydration,
                  so it isn't a shift contributor.
                */}
                <span
                  className="playlist-row-header__stat playlist-row-header__stat--episodes playlist-row-header__stat--placeholder"
                  aria-hidden="true"
                >
                  <PlaylistOutline aria-hidden="true" />
                  {' '}
                </span>
                <span
                  className="playlist-row-header__stat playlist-row-header__stat--placeholder"
                  aria-hidden="true"
                >
                  <Clock aria-hidden="true" />
                  {' '}
                </span>
              </>
            ) : (
              <>
                {episodeCount > 0 ? (
                  <span className="playlist-row-header__stat playlist-row-header__stat--episodes">
                    <PlaylistOutline />
                    {episodeCountText}
                  </span>
                ) : null}
                {totalDuration ? (
                  <span className="playlist-row-header__stat">
                    <Clock />
                    {totalDuration}
                  </span>
                ) : null}
                {renderAuthorStat()}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Outer wrapper: receives `staticText` from connectStaticText and provides
// it to the subtree so Inner's useTranslatedText calls resolve against the
// `playlistRowHeader` slice (not whatever Provider the caller's tree sets).
function CommunityPlaylistRowHeaderBase ({
  staticText,
  ...rest
}: CommunityPlaylistRowHeaderOuterProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <CommunityPlaylistRowHeaderInner {...rest} />
    </StaticTextProvider>
  )
}

export default connectStaticText({ storeKey: 'playlistRowHeader' })(React.memo(CommunityPlaylistRowHeaderBase))
